import { canUseMotion } from '../../common/motion';

import { ModalControl } from './controls/ModalControl';
import { ModalContentNavVisibilityController } from './controls/ModalContentNavVisibilityController';
import { ModalContentChooser } from './content/ModalContentChooser';
import { ModalContentCollection } from './content/ModalContentCollection';
import { ModalContentNavigator } from './content/ModalContentNavigator';
import { ModalContentIndicator } from './content/ModalContentIndicator';
import { ModalContentRenderer } from './content/ModalContentRenderer';
import { ModalContentState } from './content/ModalContentState';
import type { ModalContentItem, ModalContentNavigationAction, ModalContentNavigationType } from './content/types';
import { ModalAttachment } from './dom/ModalAttachment';
import { ModalPortal } from './dom/ModalPortal';
import { ModalSwipeHandler } from './handlers/ModalSwipeHandler';
import { ModalWheelHandler } from './handlers/ModalWheelHandler';
import type { ModalSwipeDirection, ModalWheelDirection } from './handlers/types';
import { MediaPlaybackController } from './media/MediaPlaybackController';
import type {
  ModalContentEffectName,
  ModalDisplayOptions,
  ResolvedModalDisplayOptions,
} from './options';
import {
  ATTR_MODAL_EFFECT,
  ATTR_MODAL_EFFECT_FADE,
  ATTR_MODAL_FRAME_SURFACE,
  ATTR_MODAL_CHOOSE_MODE,
  ATTR_MODAL_CHOOSE_EFFECTIVE_MODE,
  ATTR_MODAL_UI_MOTION,
  resolveModalContentNavVisibilityBreakpoint,
  resolveModalDisplayOptions,
} from './options';
import { ModalState } from './state/ModalState';

/**
 * // Class Obj: モーダルウィンドウ全体の入口処理を管理
 * // Note:
 * // - イベント登録はこのクラスに集約します。
 * // - 開閉状態、表示切替、コンテンツ描画などの実処理は各クラスへ委譲します。
 * // - ModalState 側ではイベント登録を行わず、状態遷移だけを担当します。
 * // - options を指定すると、用途別に必要な機能だけを有効化できます。
 */
export class ModalDisplay
{
  private static readonly SWIPE_NAVIGATION_TYPE_MAP: Record<ModalSwipeDirection, ModalContentNavigationType> = {
    left: 'next-item',
    right: 'prev-item',
    up: 'next-group',
    down: 'prev-group',
  };

  private static readonly WHEEL_NAVIGATION_TYPE_MAP: Record<ModalWheelDirection, ModalContentNavigationType> = {
    down: 'next-group',
    up: 'prev-group',
  };

  private static readonly ATTR_EFFECT_STATE = 'data-modal-effect-state';
  private static readonly ATTR_CONTENT_SWITCH_STATE = 'data-modal-content-switch-state';
  private static readonly ATTR_CONTENT_SWITCH_EFFECT = 'data-modal-content-switch-effect';
  private static readonly ATTR_CONTENT_SIZE_STATE = 'data-modal-content-size-state';
  private static readonly ATTR_UI_MOTION_STATE = 'data-modal-ui-motion-state';
  private static readonly SELECTOR_MODAL_CHOOSE_LIST = '[data-modal-choose-list]';
  private static readonly TRANSITION_FALLBACK_DELAY = 50;
  private static readonly CSS_MODAL_FRAME_SURFACE_BACKGROUND = '--modal-frame-surface-bg';
  private static readonly CSS_MODAL_CHOOSE_LIST_ITEMS_PER_VIEW = '--modal-choose-list-items-per-view';
  private static readonly CSS_MODAL_CHOOSE_ITEM_INLINE_SIZE = '--modal-choose-item-inline-size';
  private static readonly CSS_MODAL_UI_MOTION_MOVE_X = '--modal-ui-motion-move-x';
  private static readonly CSS_MODAL_UI_MOTION_MOVE_Y = '--modal-ui-motion-move-y';
  private static readonly CSS_MODAL_CONTENT_SWITCH_BLOCK_SIZE = '--modal-content-switch-block-size';
  private static readonly RANDOM_CONTENT_EFFECT_NAMES: ModalContentEffectName[] = ['fade', 'blur'];

  private readonly mSourceOptions: ModalDisplayOptions;
  private readonly mTargetModals: HTMLElement | null;
  private mOptions: ResolvedModalDisplayOptions;
  private readonly mModalPortal: ModalPortal;
  private readonly mModalAttachment: ModalAttachment;
  private readonly mModalControl: ModalControl;
  private readonly mModalSwipeHandler: ModalSwipeHandler;
  private readonly mModalWheelHandler: ModalWheelHandler;
  private readonly mModalContentCollection: ModalContentCollection;
  private readonly mModalContentState: ModalContentState;
  private readonly mModalContentNavigator: ModalContentNavigator;
  private readonly mModalContentChooser: ModalContentChooser;
  private readonly mModalContentIndicator: ModalContentIndicator;
  private readonly mModalContentRenderer: ModalContentRenderer;
  private readonly mModalState: ModalState;
  private readonly mMediaPlaybackController: MediaPlaybackController;
  private readonly mContentNavVisibilityController: ModalContentNavVisibilityController;
  private readonly mContentNavigationItemGetters: Record<ModalContentNavigationType, () => ModalContentItem | null>;

  private mEffectReadyFrameId: number | null = null;
  private mCloseFallbackTimerId: number | null = null;
  private mCloseTransitionEndListener: ((event: TransitionEvent) => void) | null = null;
  private mClosingModal: HTMLElement | null = null;
  private mIsClosing: boolean = false;
  private mContentSwitchFallbackTimerId: number | null = null;
  private mContentSwitchTransitionEndListener: ((event: TransitionEvent) => void) | null = null;
  private mContentSwitchDisplay: HTMLElement | null = null;
  private mContentSwitchChooseList: HTMLElement | null = null;
  private mContentSwitchFrameId: number | null = null;
  private mContentSwitchLockedDisplay: HTMLElement | null = null;
  private mContentSwitchToken: number = 0;
  private mContentSwitchModal: HTMLElement | null = null;
  private mUiMotionModal: HTMLElement | null = null;
  private readonly mManagedEventCleanupCallbacks: Array<() => void> = [];
  private mIsContentSwitching: boolean = false;
  private mIsMounted: boolean = false;

  /**
   * // Method: モーダル制御に必要な各クラスを初期化する
   * // Note:
   * // - target_modals を基準に、操作ボタン・モーダル本体・表示用コンテンツを収集します。
   * // - ナビゲーション種別ごとの取得処理は map 化し、分岐を ModalDisplay 内に散らさないようにします。
   * // - options は省略可能で、省略時は従来どおり全機能を有効にします。
   */
  constructor(
    site_body: HTMLElement,
    target_modals: HTMLElement | null,
    options: ModalDisplayOptions = {},
  )
  {
    this.mSourceOptions = options;
    this.mTargetModals = target_modals;
    this.mOptions = resolveModalDisplayOptions(this.mSourceOptions, this.mTargetModals);

    this.mModalPortal = new ModalPortal();
    this.mModalAttachment = new ModalAttachment(site_body);
    this.mModalControl = new ModalControl(target_modals, this.mModalPortal);
    this.mModalSwipeHandler = new ModalSwipeHandler();
    this.mModalWheelHandler = new ModalWheelHandler();
    this.mMediaPlaybackController = new MediaPlaybackController();
    this.mModalContentCollection = new ModalContentCollection(target_modals);
    this.mModalContentState = new ModalContentState();
    this.mModalContentNavigator = new ModalContentNavigator(
      this.mModalContentCollection,
      this.mModalContentState,
    );
    this.mModalContentChooser = new ModalContentChooser(
      this.mModalContentCollection,
      this.mModalContentState,
      this.mOptions.choose,
    );
    this.mModalContentIndicator = new ModalContentIndicator(
      this.mModalContentCollection,
      this.mOptions.indicator,
    );
    this.mModalContentRenderer = new ModalContentRenderer();
    this.mModalState = new ModalState(this.mModalAttachment, this.mModalControl, this.mModalPortal);
    this.mContentNavVisibilityController = new ModalContentNavVisibilityController(
      this.mModalControl,
      this.mMediaPlaybackController,
      this.mOptions.contentNavVisibility,
    );

    this.mContentNavigationItemGetters = {
      'next-item': () => this.mModalContentNavigator.getNextItem(this.mOptions.loop.item),
      'prev-item': () => this.mModalContentNavigator.getPrevItem(this.mOptions.loop.item),
      'next-group': () => this.mModalContentNavigator.getNextGroup(this.mOptions.loop.group),
      'prev-group': () => this.mModalContentNavigator.getPrevGroup(this.mOptions.loop.group),
    };
  }

  /**
   * // Method: モーダル関連のイベント登録を実行する
   * // Note:
   * // - open / close / window切替は常に登録します。
   * // - item選択 / content navigation / swipe / wheel / 遅延表示は options に応じて登録します。
   */
  public runProgram(): void
  {
    if (this.mIsMounted)
    {
      this.destroy();
    }

    this.mIsMounted = true;

    this.applyMotionPreference();
    this.applyFrameAttributes();
    this.applyEffectAttributes();
    this.applyContentChooserAttributes();
    this.applyUiMotionAttributes();
    this.mMediaPlaybackController.prepareVideoThumbnailsInElements(
      this.mModalControl.mOpenButtons,
    );
    this.mModalContentChooser.createLists(this.mModalControl.getAllModals());
    this.bindResponsiveChange();

    this.bindOpen(this.mModalControl.mOpenButtons);
    this.bindClose(this.mModalControl.mCloseButtons);
    this.bindOverlayClose(this.mModalAttachment.mOverlay);
    this.bindBackdropClose(this.mModalControl.getAllModals());
    this.bindEscapeClose();

    this.bindSwitch(this.mModalControl.mPrevButtons);
    this.bindSwitch(this.mModalControl.mNextButtons);

    this.bindContentChoice(this.mModalControl.getAllModals());

    if (this.mOptions.contentNavButton.enabled)
    {
      this.bindContentNavigation(this.mModalControl.mContentNavButtons);
    }

    if (this.mOptions.swipe.enabled)
    {
      this.bindSwipeNavigation(this.mModalControl.getAllModals());
    }

    if (this.mOptions.wheel.enabled && this.mOptions.wheel.group)
    {
      this.bindWheelNavigation(this.mModalControl.getAllModals());
    }

    if (this.mOptions.contentNavButton.enabled)
    {
      this.mContentNavVisibilityController.bindPointerInteraction(this.mModalControl.getAllModals());
    }
  }

  /**
   * // Method: ModalDisplay が登録・生成・付与した状態を破棄する
   * // Note:
   * // - remount 時にイベントリスナー、timer、frame、生成DOM、portal状態が残らないようにします。
   * // - close animation は待たず、破棄時は即時復帰へ寄せます。
   */
  public destroy(): void
  {
    const target_modals: HTMLElement[] = this.mModalControl.getAllModals();

    this.clearManagedEventListeners();
    this.clearEffectReadyFrame();
    this.cancelCloseEffect();
    this.cancelContentSwitchEffect();
    this.mContentNavVisibilityController.destroy();
    this.mModalSwipeHandler.destroy();
    this.mModalWheelHandler.destroy();
    this.mMediaPlaybackController.destroy();

    target_modals.forEach((modal: HTMLElement) =>
    {
      this.mContentNavVisibilityController.hide(modal);
      this.mMediaPlaybackController.stopVideosInElement(
        modal,
        this.mOptions.video.resetOnChange,
      );
      this.mModalContentRenderer.clear(modal);
    });

    this.mModalContentChooser.destroy(target_modals);
    this.mModalContentIndicator.destroy(target_modals);
    this.mModalState.closeAll();
    this.mModalContentState.reset();
    this.resetAllModalEffectStates();
    this.clearAppliedAttributes();

    this.mIsClosing = false;
    this.mIsContentSwitching = false;
    this.mIsMounted = false;
  }

  /**
   * // Method: ユーザーの motion 設定に応じた状態属性を同期する
   * // Note:
   * // - reduced motion 時は data-motion-reduced="true" を付与します。
   * // - 実際の transition / animation 無効化は SCSS 側で制御します。
   */
  private applyMotionPreference(): void
  {
    const can_use_motion: boolean = canUseMotion(this.mOptions.respectUserMotionPreference);
    const should_reduce_motion: boolean = !can_use_motion;

    this.toggleMotionReducedAttribute(this.mModalControl.mModalRoot, should_reduce_motion);
    this.toggleMotionReducedAttribute(this.mModalAttachment.mOverlay, should_reduce_motion);

    this.mModalControl.getAllModals().forEach((modal: HTMLElement) =>
    {
      this.toggleMotionReducedAttribute(modal, should_reduce_motion);
    });
  }

  private toggleMotionReducedAttribute(element: HTMLElement | null, should_reduce_motion: boolean): void
  {
    if (!element)
    {
      return;
    }

    if (should_reduce_motion)
    {
      element.setAttribute('data-motion-reduced', 'true');
      return;
    }

    element.removeAttribute('data-motion-reduced');
  }

  /**
   * // Method: frame 設定をモーダル要素へ同期する
   * // Note:
   * // - surface.enabled: true の場合だけ data属性と背景色CSS変数を付与します。
   * // - 初期値は背景面なしのため、既存の透過的な見た目を維持します。
   */
  private applyFrameAttributes(): void
  {
    this.mModalControl.getAllModals().forEach((modal: HTMLElement) =>
    {
      this.applyFrameAttributesToElement(modal);
    });
  }

  /**
   * // Method: 指定要素へ frame 用 data属性とCSS変数を反映する
   */
  private applyFrameAttributesToElement(element: HTMLElement): void
  {
    if (!this.mOptions.frame.surface.enabled)
    {
      element.removeAttribute(ATTR_MODAL_FRAME_SURFACE);
      element.style.removeProperty(ModalDisplay.CSS_MODAL_FRAME_SURFACE_BACKGROUND);
      return;
    }

    element.setAttribute(ATTR_MODAL_FRAME_SURFACE, 'true');
    element.style.setProperty(
      ModalDisplay.CSS_MODAL_FRAME_SURFACE_BACKGROUND,
      this.mOptions.frame.surface.background,
    );
  }

  /**
   * // Method: choose.mode と choose.list 設定をモーダル要素へ同期する
   * // Note:
   * // - data-modal-choose-mode は option 上の希望モードです。
   * // - data-modal-choose-effective-mode は responsive rule 適用後の実効モードです。
   * // - list.itemsPerView は表示領域内に見せる choose item 数としてCSS変数へ反映します。
   */
  private applyContentChooserAttributes(): void
  {
    const effective_mode = this.mOptions.choose.mode;

    const choose_list_items_per_view: number = this.mOptions.choose.list.itemsPerView;
    const choose_item_inline_size: string = this.createChooseItemInlineSizeValue(
      choose_list_items_per_view,
    );

    this.mModalControl.getAllModals().forEach((modal: HTMLElement) =>
    {
      modal.setAttribute(ATTR_MODAL_CHOOSE_MODE, this.mOptions.choose.mode);
      modal.setAttribute(ATTR_MODAL_CHOOSE_EFFECTIVE_MODE, effective_mode);
      modal.style.setProperty(
        ModalDisplay.CSS_MODAL_CHOOSE_LIST_ITEMS_PER_VIEW,
        String(choose_list_items_per_view),
      );
      modal.style.setProperty(
        ModalDisplay.CSS_MODAL_CHOOSE_ITEM_INLINE_SIZE,
        choose_item_inline_size,
      );
    });
  }

  /**
   * // Method: choose item の表示幅用CSS値を生成する
   * // Note:
   * // - bottom配置では、itemsPerView 数だけ表示領域内に見せ、超過分は横スクロールで閲覧します。
   * // - CSSの未対応環境差を避けるため、calc()内では乗算を使わずgapを加減算で組み立てます。
   */
  private createChooseItemInlineSizeValue(items_per_view_count: number): string
  {
    if (items_per_view_count <= 1)
    {
      return '100%';
    }

    const gap_size_terms: string[] = Array.from(
      { length: items_per_view_count - 1 },
      () => 'var(--modal-choose-list-gap)',
    );

    return `calc((100% - ${gap_size_terms.join(' - ')}) / ${items_per_view_count})`;
  }

  /**
   * // Method: top-level responsive の breakpoint 変化を監視する
   * // Note:
   * // - 各 rule の max-width 境界だけを監視し、resizeごとの直接処理は避けます。
   * // - breakpoint をまたいだら options を再解決し、choose list の表示数・mode を同期します。
   */
  private bindResponsiveChange(): void
  {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
    {
      return;
    }

    const breakpoint_values: number[] = this.getResponsiveBreakpointValues();

    if (breakpoint_values.length === 0)
    {
      return;
    }

    breakpoint_values.forEach((breakpoint: number) =>
    {
      const media_query_list: MediaQueryList = window.matchMedia(
        `(max-width: ${breakpoint}px)`,
      );

      const change_listener = (): void =>
      {
        this.handleResponsiveChange();
      };

      this.addManagedEventListener(media_query_list, 'change', change_listener);
    });
  }

  /**
   * // Method: responsive 監視に使う breakpoint 値を取得する
   */
  private getResponsiveBreakpointValues(): number[]
  {
    const breakpoint_values: number[] = [];

    this.mSourceOptions.responsive?.forEach((responsive_rule) =>
    {
      this.addResponsiveBreakpointValue(breakpoint_values, responsive_rule.breakpoint);
    });

    if (this.mSourceOptions.contentNavVisibility?.enabled === undefined)
    {
      this.addResponsiveBreakpointValue(
        breakpoint_values,
        resolveModalContentNavVisibilityBreakpoint(this.mTargetModals),
      );
    }

    return breakpoint_values;
  }

  /**
   * // Method: responsive 監視対象の breakpoint 値を追加する
   */
  private addResponsiveBreakpointValue(
    breakpoint_values: number[],
    breakpoint: number | undefined,
  ): void
  {
    if (typeof breakpoint !== 'number' || !Number.isFinite(breakpoint) || breakpoint < 0)
    {
      return;
    }

    const normalized_breakpoint: number = Math.floor(breakpoint);

    if (breakpoint_values.includes(normalized_breakpoint))
    {
      return;
    }

    breakpoint_values.push(normalized_breakpoint);
  }

  /**
   * // Method: responsive rule の再解決を現在表示へ反映する
   */
  private handleResponsiveChange(): void
  {
    this.mOptions = resolveModalDisplayOptions(this.mSourceOptions, this.mTargetModals);
    this.mContentNavVisibilityController.updateOptions(this.mOptions.contentNavVisibility);
    this.mModalContentChooser.updateOptions(this.mOptions.choose);

    this.applyMotionPreference();
    this.applyFrameAttributes();
    this.applyEffectAttributes();
    this.applyContentChooserAttributes();
    this.applyUiMotionAttributes();
    this.mModalContentChooser.createLists(this.mModalControl.getAllModals());

    const current_item: ModalContentItem | null = this.mModalContentState.getCurrentItem();

    if (!current_item)
    {
      this.mModalContentChooser.clear(this.mModalControl.getAllModals());
      return;
    }

    const active_modal: HTMLElement | null = this.getActiveModal();

    if (!active_modal)
    {
      return;
    }

    this.mModalContentChooser.update(active_modal, current_item);
    this.updateContentNavButtonAvailability(active_modal);

    const content_display: HTMLElement | null =
      this.mModalContentRenderer.getContentDisplay(active_modal);

    this.mContentNavVisibilityController.startByRenderedContent(active_modal, content_display);
  }

  /**
   * // Method: effect 設定をモーダル要素へ同期する
   * // Note:
   * // - TS option で指定した window effect を SCSS 側から参照できるようにします。
   * // - 主 effect が none でも fade: true の場合は、fade用属性だけを付与します。
   */
  private applyEffectAttributes(): void
  {
    const effect_name: string = this.mOptions.effect.window.name;
    const should_apply_effect: boolean = this.shouldUseModalPrimaryEffect();
    const should_apply_fade: boolean = this.shouldUseModalFadeEffect();

    this.mModalControl.getAllModals().forEach((modal: HTMLElement) =>
    {
      this.applyEffectAttributesToElement(
        modal,
        effect_name,
        should_apply_effect,
        should_apply_fade,
      );
    });
  }

  /**
   * // Method: 指定要素へ effect 用 data 属性を反映する
   */
  private applyEffectAttributesToElement(
    element: HTMLElement | null,
    effect_name: string,
    should_apply_effect: boolean,
    should_apply_fade: boolean,
  ): void
  {
    if (!element)
    {
      return;
    }

    if (should_apply_effect)
    {
      element.setAttribute(ATTR_MODAL_EFFECT, effect_name);
    }
    else
    {
      element.removeAttribute(ATTR_MODAL_EFFECT);
    }

    if (should_apply_fade)
    {
      element.setAttribute(ATTR_MODAL_EFFECT_FADE, 'true');
    }
    else
    {
      element.removeAttribute(ATTR_MODAL_EFFECT_FADE);
    }
  }

  /**
   * // Method: UI motion 設定をモーダル要素へ同期する
   * // Note:
   * // - enabled: true の場合だけ data属性と移動量CSS変数を付与します。
   * // - duration / easing はSCSS側のCSS変数で調整します。
   */
  private applyUiMotionAttributes(): void
  {
    this.mModalControl.getAllModals().forEach((modal: HTMLElement) =>
    {
      this.applyUiMotionAttributesToElement(modal);
    });
  }

  /**
   * // Method: 指定要素へ UI motion 用 data 属性とCSS変数を反映する
   */
  private applyUiMotionAttributesToElement(element: HTMLElement): void
  {
    if (!this.mOptions.uiMotion.enabled)
    {
      element.removeAttribute(ATTR_MODAL_UI_MOTION);
      element.style.removeProperty(ModalDisplay.CSS_MODAL_UI_MOTION_MOVE_X);
      element.style.removeProperty(ModalDisplay.CSS_MODAL_UI_MOTION_MOVE_Y);
      return;
    }

    element.setAttribute(ATTR_MODAL_UI_MOTION, 'true');
    element.style.setProperty(
      ModalDisplay.CSS_MODAL_UI_MOTION_MOVE_X,
      this.mOptions.uiMotion.moveX,
    );
    element.style.setProperty(
      ModalDisplay.CSS_MODAL_UI_MOTION_MOVE_Y,
      this.mOptions.uiMotion.moveY,
    );
  }

  /**
   * // Method: destroy 時に解除できる event listener として登録する
   */
  private addManagedEventListener(
    target: EventTarget,
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void
  {
    target.addEventListener(type, listener, options);
    this.mManagedEventCleanupCallbacks.push((): void =>
    {
      target.removeEventListener(type, listener, options);
    });
  }

  /**
   * // Method: ModalDisplay で登録した event listener をすべて解除する
   */
  private clearManagedEventListeners(): void
  {
    this.mManagedEventCleanupCallbacks.forEach((cleanup_callback: () => void) =>
    {
      cleanup_callback();
    });

    this.mManagedEventCleanupCallbacks.length = 0;
  }

  /**
   * // Method: options 同期時に付与した属性・CSS変数を解除する
   */
  private clearAppliedAttributes(): void
  {
    const target_elements: HTMLElement[] = [
      ...(this.mModalControl.mModalRoot ? [this.mModalControl.mModalRoot] : []),
      ...this.mModalControl.getAllModals(),
      this.mModalAttachment.mOverlay,
    ];

    target_elements.forEach((element: HTMLElement) =>
    {
      element.removeAttribute('data-motion-reduced');
      element.removeAttribute(ATTR_MODAL_EFFECT);
      element.removeAttribute(ATTR_MODAL_EFFECT_FADE);
      element.removeAttribute(ATTR_MODAL_FRAME_SURFACE);
      element.removeAttribute(ATTR_MODAL_CHOOSE_MODE);
      element.removeAttribute(ATTR_MODAL_CHOOSE_EFFECTIVE_MODE);
      element.removeAttribute(ATTR_MODAL_UI_MOTION);
      element.style.removeProperty(ModalDisplay.CSS_MODAL_FRAME_SURFACE_BACKGROUND);
      element.style.removeProperty(ModalDisplay.CSS_MODAL_CHOOSE_LIST_ITEMS_PER_VIEW);
      element.style.removeProperty(ModalDisplay.CSS_MODAL_CHOOSE_ITEM_INLINE_SIZE);
      element.style.removeProperty(ModalDisplay.CSS_MODAL_UI_MOTION_MOVE_X);
      element.style.removeProperty(ModalDisplay.CSS_MODAL_UI_MOTION_MOVE_Y);
      element.style.removeProperty(ModalDisplay.CSS_MODAL_CONTENT_SWITCH_BLOCK_SIZE);
    });
  }

  /**
   * // Method: 複数要素へ click イベントを登録する共通処理
   * // Note:
   * // - 各 bind メソッドのイベント登録処理を揃えるための小さな共通処理です。
   */
  private bindClickEvent(
    target_elements: HTMLElement[],
    callback: (element: HTMLElement) => void,
  ): void
  {
    target_elements.forEach((element: HTMLElement) =>
    {
      const click_listener = (): void =>
      {
        callback(element);
      };

      this.addManagedEventListener(element, 'click', click_listener);
    });
  }

  /**
   * // Method: モーダルを開くボタンのイベントを登録する
   * // Note:
   * // - モーダル本体を開いた後、汎用コンテンツを反映します。
   */
  private bindOpen(open_buttons: HTMLElement[]): void
  {
    this.bindClickEvent(
      open_buttons,
      (open_button: HTMLElement) =>
      {
        this.cancelCloseEffect();

        const target_modal: HTMLElement | null = this.prepareOpenEffectByElement(open_button);

        this.mModalState.openByElement(open_button);

        this.renderContentByElement(open_button, false);
        this.activateOpenEffect(target_modal);
      },
    );
  }

  /**
   * // Method: モーダルを閉じるボタンのイベントを登録する
   * // Note:
   * // - close は個別閉じではなく、全モーダルを閉じる処理へ統一しています。
   */
  private bindClose(close_buttons: HTMLElement[]): void
  {
    this.bindClickEvent(
      close_buttons,
      (_element: HTMLElement) =>
      {
        this.closeAll();
      },
    );
  }

  /**
   * // Method: オーバーレイクリック時の閉じるイベントを登録する
   */
  private bindOverlayClose(target_element: HTMLElement | null): void
  {
    if (!target_element)
    {
      return;
    }

    const click_listener = (): void =>
    {
      this.closeAll();
    };

    this.addManagedEventListener(target_element, 'click', click_listener);
  }


  /**
   * // Method: モーダル外側クリック時の閉じるイベントを登録する
   * // Note:
   * // - #[data-modal] は #modalOverlay より前面にあるため、見た目上の overlay 部分は [data-modal] 自体がクリックを受け取ります。
   * // - .window 内の操作やコンテンツクリックでは閉じないよう、背景部分への直接クリックだけを対象にします。
   */
  private bindBackdropClose(target_modals: HTMLElement[]): void
  {
    target_modals.forEach((modal: HTMLElement) =>
    {
      const click_listener = (event: Event): void =>
      {
        const target_element: EventTarget | null = event.target;

        if (target_element !== modal)
        {
          return;
        }

        this.closeAll();
      };

      this.addManagedEventListener(modal, 'click', click_listener);
    });
  }

  /**
   * // Method: Escキーでモーダルを閉じるイベントを登録する
   * // Note:
   * // - モーダルが開いている時だけ closeAll() を実行します。
   * // - document に登録することで、フォーカス位置に関係なく Esc 操作を受け取ります。
   */
  private bindEscapeClose(): void
  {
    const keydown_listener = (event: Event): void =>
    {
      if (!(event instanceof KeyboardEvent) || event.key !== 'Escape')
      {
        return;
      }

      if (!this.hasActiveModal())
      {
        return;
      }

      event.preventDefault();

      this.closeAll();
    };

    this.addManagedEventListener(document, 'keydown', keydown_listener);
  }

  /**
   * // Method: 管理対象の中に表示中のモーダルがあるか判定する
   * // Note:
   * // - Escキーなど、現在モーダルが開いている時だけ実行したい処理の判定に使います。
   */
  private hasActiveModal(): boolean
  {
    return this.mModalControl.getAllModals().some((modal: HTMLElement) =>
    {
      return this.mModalControl.isActiveModal(modal);
    });
  }

  /**
   * // Method: 開く前に effect 用の状態属性を初期化する
   * // Note:
   * // - display: none からの transition を安定させるため、data-modal-state 付与前に ready 状態を外します。
   * // - 実際の開閉表示は既存の ModalState / ModalControl に委譲します。
   */
  private prepareOpenEffectByElement(element: HTMLElement): HTMLElement | null
  {
    const target_modal: HTMLElement | null = this.mModalControl.getTargetModalByElement(element);

    if (!this.shouldUseModalEffect() || !target_modal)
    {
      return target_modal;
    }

    this.clearEffectReadyFrame();
    this.resetModalEffectState(target_modal);

    return target_modal;
  }

  /**
   * // Method: 開く時の effect ready 状態を反映する
   * // Note:
   * // - requestAnimationFrame を2回挟み、display切替後の初期状態を確定させてから transition を開始します。
   */
  private activateOpenEffect(target_modal: HTMLElement | null): void
  {
    if (!this.shouldUseModalEffect() || !target_modal)
    {
      return;
    }

    this.clearEffectReadyFrame();

    this.mEffectReadyFrameId = window.requestAnimationFrame(() =>
    {
      this.mEffectReadyFrameId = window.requestAnimationFrame(() =>
      {
        target_modal.setAttribute(ModalDisplay.ATTR_EFFECT_STATE, 'ready');
        this.mEffectReadyFrameId = null;
      });
    });
  }

  /**
   * // Method: モーダル開閉effectを使う設定か判定する
   */
  private shouldUseModalEffect(): boolean
  {
    return this.shouldUseModalPrimaryEffect() || this.shouldUseModalFadeEffect();
  }

  /**
   * // Method: scale / blur などの主 effect を使う設定か判定する
   */
  private shouldUseModalPrimaryEffect(): boolean
  {
    return this.mOptions.effect.window.enabled && this.mOptions.effect.window.name !== 'none';
  }

  /**
   * // Method: opacity fade effect を使う設定か判定する
   */
  private shouldUseModalFadeEffect(): boolean
  {
    return this.mOptions.effect.window.enabled && this.mOptions.effect.window.fade;
  }

  /**
   * // Method: effect ready 状態を解除する
   */
  private resetModalEffectState(modal: HTMLElement): void
  {
    modal.removeAttribute(ModalDisplay.ATTR_EFFECT_STATE);
  }

  /**
   * // Method: 全モーダルの effect ready 状態を解除する
   */
  private resetAllModalEffectStates(): void
  {
    this.mModalControl.getAllModals().forEach((modal: HTMLElement) =>
    {
      this.resetModalEffectState(modal);
    });
  }

  /**
   * // Method: effect ready 反映待ちの frame を解除する
   */
  private clearEffectReadyFrame(): void
  {
    if (this.mEffectReadyFrameId === null)
    {
      return;
    }

    window.cancelAnimationFrame(this.mEffectReadyFrameId);
    this.mEffectReadyFrameId = null;
  }

  /**
   * // Method: モーダルウィンドウ自体の前後切替イベントを登録する
   * // Note:
   * // - data-modal-prev / data-modal-next は、表示するモーダル本体の切替に使います。
   * // - 汎用コンテンツの next-item 等とは役割を分けています。
   */
  private bindSwitch(switch_buttons: HTMLElement[]): void
  {
    this.bindClickEvent(
      switch_buttons,
      (element: HTMLElement) =>
      {
        this.cancelCloseEffect();

        const target_modal: HTMLElement | null = this.prepareOpenEffectByElement(element);

        this.mModalControl.changeWindowByElement(element);
        this.activateOpenEffect(target_modal);
      },
    );
  }

  /**
   * // Method: モーダル内の選択リストクリックイベントを登録する
   * // Note:
   * // - 選択リスト自体の解決・イベント委譲は ModalContentChooser に委譲します。
   * // - ModalDisplay は選択された content item を描画する入口だけを担当します。
   */
  private bindContentChoice(target_modals: HTMLElement[]): void
  {
    this.mModalContentChooser.bind(
      target_modals,
      (content_item: ModalContentItem, modal: HTMLElement) =>
      {
        this.renderContentItem(content_item, modal);
      },
    );
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションのイベントを登録する
   * // Note:
   * // - next / prev は、現在 group の item 数に応じて item / group 移動へ解決します。
   * // - next-item / prev-item は、choose item がある構成では具体的な item 指定として扱います。
   * // - choose.mode 未指定かつ現在 group に item が1件だけの場合は、simple modal として group 移動へ補正します。
   * // - next-group / prev-group は、従来どおり具体的な group 指定として扱います。
   * // - options で item / group を無効化した場合、その種別のボタン操作は無視します。
   */
  private bindContentNavigation(nav_buttons: HTMLElement[]): void
  {
    this.bindClickEvent(
      nav_buttons,
      (element: HTMLElement) =>
      {
        this.renderContentByNavigationElement(element);
      },
    );
  }

  /**
   * // Method: モーダル内のスワイプによるコンテンツ移動イベントを登録する
   * // Note:
   * // - モーダルが data-modal-state="active" のときだけ反応します。
   * // - 左右スワイプは item 移動、上下スワイプは group 移動として扱います。
   * // - choose list 起点の touch 操作は、リスト本体の横スクロールを優先して除外します。
   * // - options で item / group 単位のスワイプを個別に無効化できます。
   */
  private bindSwipeNavigation(target_modals: HTMLElement[]): void
  {
    target_modals.forEach((modal: HTMLElement) =>
    {
      this.mModalSwipeHandler.bind(
        modal,
        (swipe_direction: ModalSwipeDirection) =>
        {
          if (!this.mModalControl.isActiveModal(modal))
          {
            return;
          }

          const navigation_type: ModalContentNavigationType | null =
            this.resolveContentNavigationTypeBySwipeDirection(swipe_direction);

          if (!navigation_type)
          {
            return;
          }

          this.renderContentByNavigationType(navigation_type, modal);
        },
        () =>
        {
          return this.mModalControl.isActiveModal(modal);
        },
        (touch_event: TouchEvent) =>
        {
          return this.isEventFromChooseList(touch_event, modal);
        },
      );
    });
  }

  /**
   * // Method: モーダル内のホイール操作による group 移動イベントを登録する
   * // Note:
   * // - PC の上下スクロール操作のみを対象にします。
   * // - choose list 起点の wheel 操作は、リスト本体のスクロールを優先して除外します。
   * // - 下方向は next-group、上方向は prev-group として扱います。
   */
  private bindWheelNavigation(target_modals: HTMLElement[]): void
  {
    target_modals.forEach((modal: HTMLElement) =>
    {
      this.mModalWheelHandler.bind(
        modal,
        (wheel_direction: ModalWheelDirection) =>
        {
          if (!this.mModalControl.isActiveModal(modal))
          {
            return;
          }

          const navigation_type: ModalContentNavigationType | null =
            this.resolveContentNavigationTypeByWheelDirection(wheel_direction);

          if (!navigation_type)
          {
            return;
          }

          this.renderContentByNavigationType(navigation_type, modal);
        },
        (wheel_event: WheelEvent) =>
        {
          return this.isEventFromChooseList(wheel_event, modal);
        },
      );
    });
  }

  /**
   * // Method: 操作イベントが choose list 内から発生したか判定する
   * // Note:
   * // - choose list は横スクロール可能な独立UIとして扱います。
   * // - modal 本体に登録した swipe / wheel navigation が、choose list の操作を奪わないようにします。
   */
  private isEventFromChooseList(event: Event, modal: HTMLElement): boolean
  {
    const target_element: Element | null = event.target instanceof Element ? event.target : null;

    if (!target_element)
    {
      return false;
    }

    const choose_list: HTMLElement | null = target_element.closest<HTMLElement>(
      ModalDisplay.SELECTOR_MODAL_CHOOSE_LIST,
    );

    return choose_list !== null && modal.contains(choose_list);
  }

  /**
   * // Method: スワイプ方向を汎用コンテンツ用ナビゲーション種別へ変換する
   * // Note:
   * // - 方向と移動先の対応を map 化し、分岐の重複を避けます。
   * // - options で無効な方向は null を返し、移動処理を行いません。
   */
  private resolveContentNavigationTypeBySwipeDirection(
    swipe_direction: ModalSwipeDirection,
  ): ModalContentNavigationType | null
  {
    if (!this.isSwipeDirectionEnabled(swipe_direction))
    {
      return null;
    }

    return ModalDisplay.SWIPE_NAVIGATION_TYPE_MAP[swipe_direction];
  }

  /**
   * // Method: ホイール方向を汎用コンテンツ用ナビゲーション種別へ変換する
   * // Note:
   * // - wheel は group 移動だけを担当するため、上下方向のみを変換します。
   */
  private resolveContentNavigationTypeByWheelDirection(
    wheel_direction: ModalWheelDirection,
  ): ModalContentNavigationType | null
  {
    if (!this.mOptions.wheel.enabled || !this.mOptions.wheel.group)
    {
      return null;
    }

    return ModalDisplay.WHEEL_NAVIGATION_TYPE_MAP[wheel_direction];
  }

  /**
   * // Method: 指定スワイプ方向が設定上有効か判定する
   * // Note:
   * // - left / right は item、up / down は group として扱います。
   */
  private isSwipeDirectionEnabled(swipe_direction: ModalSwipeDirection): boolean
  {
    if (!this.mOptions.swipe.enabled)
    {
      return false;
    }

    if (swipe_direction === 'left' || swipe_direction === 'right')
    {
      return this.mOptions.swipe.item;
    }

    return this.mOptions.swipe.group;
  }

  /**
   * // Method: 指定ナビゲーション種別のボタン操作が設定上有効か判定する
   * // Note:
   * // - ここでは data-modal-content-nav を持つボタン操作だけを判定します。
   * // - swipe / wheel からの移動は、それぞれ別の option で制御します。
   */
  private isContentNavButtonNavigationTypeEnabled(
    navigation_type: ModalContentNavigationType,
  ): boolean
  {
    if (!this.mOptions.contentNavButton.enabled)
    {
      return false;
    }

    if (navigation_type === 'next-item' || navigation_type === 'prev-item')
    {
      return this.mOptions.contentNavButton.item;
    }

    return this.mOptions.contentNavButton.group;
  }

  /**
   * // Method: data-modal-content-nav の指定値を具体的な移動種別へ解決する
   * // Note:
   * // - next / prev は抽象指定として扱います。
   * // - next-item / prev-item は、choose item がある構成では具体的な item 指定として扱います。
   * // - choose.mode 未指定かつ現在 group に item が1件だけの場合は、simple modal として group 移動へ補正します。
   * // - next-group / prev-group は、従来どおり具体的な group 指定として扱います。
   */
  private resolveContentNavigationTypeByAction(
    navigation_action: ModalContentNavigationAction | null,
  ): ModalContentNavigationType | null
  {
    if (!navigation_action)
    {
      return null;
    }

    if (navigation_action === 'next' || navigation_action === 'prev')
    {
      return this.resolveAbstractContentNavigationType(navigation_action);
    }

    if (navigation_action === 'next-item' || navigation_action === 'prev-item')
    {
      return this.resolveItemContentNavigationType(navigation_action);
    }

    return navigation_action;
  }

  /**
   * // Method: 抽象 prev / next を現在構成に合う item / group 移動へ解決する
   * // Note:
   * // - choose list UI のDOM有無ではなく、現在 group に複数 item があるかどうかを基準にします。
   * // - choose.mode は choose list UI の表示設定であり、item 移動の可否は決めません。
   */
  private resolveAbstractContentNavigationType(
    navigation_action: 'next' | 'prev',
  ): ModalContentNavigationType
  {
    const navigation_unit: 'item' | 'group' =
      this.shouldResolveAbstractContentNavigationToItem() ? 'item' : 'group';

    return `${navigation_action}-${navigation_unit}` as ModalContentNavigationType;
  }

  /**
   * // Method: 明示 item 指定を現在構成に合う移動種別へ解決する
   * // Note:
   * // - choose.mode 指定時は、prev-item / next-item を item 移動として扱います。
   * // - choose.mode 未指定かつ現在 group に item が1件だけの場合は、simple modal として group 移動へ補正します。
   * // - choose item がある構成で端に到達した場合は、group 移動へ fallback せず移動不可にします。
   */
  private resolveItemContentNavigationType(
    navigation_type: Extract<ModalContentNavigationType, 'next-item' | 'prev-item'>,
  ): ModalContentNavigationType
  {
    if (this.shouldResolveItemContentNavigationToGroup())
    {
      return navigation_type === 'next-item' ? 'next-group' : 'prev-group';
    }

    return navigation_type;
  }

  /**
   * // Method: 明示 item 指定を simple modal 用の group 移動へ補正するか判定する
   */
  private shouldResolveItemContentNavigationToGroup(): boolean
  {
    if (this.mOptions.choose.isModeExplicit)
    {
      return false;
    }

    return !this.shouldResolveAbstractContentNavigationToItem();
  }

  /**
   * // Method: 抽象 prev / next を item 移動として扱うか判定する
   */
  private shouldResolveAbstractContentNavigationToItem(): boolean
  {
    const current_item: ModalContentItem | null = this.mModalContentState.getCurrentItem();

    if (!current_item)
    {
      return false;
    }

    return this.mModalContentCollection.getItemCountByGroup(current_item.group_name) > 1;
  }

  /**
   * // Method: クリック元に紐づく汎用コンテンツをモーダルへ描画する
   * // Note:
   * // - open ボタンや選択ボタンなど、要素起点で表示対象を探す場合に使います。
   */
  private renderContentByElement(element: HTMLElement, should_animate: boolean = true): void
  {
    const content_item: ModalContentItem | null =
      this.mModalContentCollection.getItemByElement(element);

    if (!content_item)
    {
      return;
    }

    const target_modal: HTMLElement | null = this.mModalControl.getRelatedModalByElement(element);

    this.renderContentItem(content_item, target_modal, should_animate);
  }

  /**
   * // Method: ナビゲーションボタンに紐づく汎用コンテンツをモーダルへ描画する
   * // Note:
   * // - data-modal-content-nav の値を読み取り、移動先 item を解決してから描画します。
   * // - next / prev の抽象指定は、ここで具体的な item / group 移動へ変換します。
   * // - options で無効化されたボタン操作はここで止めます。
   */
  private renderContentByNavigationElement(element: HTMLElement): void
  {
    const navigation_action: ModalContentNavigationAction | null =
      this.mModalControl.getContentNavigationActionByElement(element);
    const target_modal: HTMLElement | null = this.mModalControl.getRelatedModalByElement(element);
    const navigation_type: ModalContentNavigationType | null =
      this.resolveContentNavigationTypeByAction(navigation_action);

    if (!navigation_type)
    {
      return;
    }

    if (!this.isContentNavButtonNavigationTypeEnabled(navigation_type))
    {
      return;
    }

    this.renderContentByNavigationType(navigation_type, target_modal);
  }

  /**
   * // Method: 指定ナビゲーション種別に応じて次に表示する汎用コンテンツを描画する
   */
  private renderContentByNavigationType(
    navigation_type: ModalContentNavigationType,
    target_modal: HTMLElement | null,
  ): void
  {
    const content_item: ModalContentItem | null =
      this.getContentItemByNavigationType(navigation_type);

    if (!content_item)
    {
      return;
    }

    this.renderContentItem(content_item, target_modal);
  }

  /**
   * // Method: 指定した汎用コンテンツをモーダルへ描画する
   * // Note:
   * // - 要素起点 / ナビゲーション起点の描画処理をここへ集約します。
   * // - 描画前に既存 video を停止し、描画後に設定に応じて新しい video の再生を試みます。
   */
  private renderContentItem(
    content_item: ModalContentItem,
    target_modal: HTMLElement | null,
    should_animate: boolean = true,
  ): void
  {
    if (this.mIsContentSwitching)
    {
      return;
    }

    const content_display: HTMLElement | null =
      this.mModalContentRenderer.getContentDisplay(target_modal);
    const choose_list: HTMLElement | null = this.getContentSwitchChooseList(
      target_modal,
      content_item,
      should_animate,
    );

    if (!this.shouldUseContentSwitchEffect(content_display, should_animate))
    {
      this.renderContentItemImmediately(
        content_item,
        target_modal,
      );

      return;
    }

    this.mIsContentSwitching = true;
    this.mContentSwitchModal = target_modal;
    this.updateContentNavButtonAvailability(target_modal);

    const content_switch_token: number = this.createContentSwitchToken();
    this.mContentSwitchChooseList = choose_list;
    this.lockContentSwitchDisplaySize(content_display);
    this.startUiMotion(target_modal);
    const content_switch_effect_name: ModalContentEffectName =
      this.resolveContentSwitchEffectName();
    this.addContentSwitchEffectAttributes(content_display, content_switch_effect_name);

    if (choose_list)
    {
      this.addContentSwitchEffectAttributes(choose_list, content_switch_effect_name);
    }

    this.waitForContentSwitchFadeOut(content_display, () =>
    {
      const rendered_display: HTMLElement | null = this.renderContentItemImmediately(
        content_item,
        target_modal,
      );

      this.activateContentSwitchFadeInWhenReady(rendered_display, content_switch_token);
    });
  }

  /**
   * // Method: 指定した汎用コンテンツを即時描画する
   * // Note:
   * // - content switch fade の有無に関係なく、実際の clone 差し替え処理はここへ集約します。
   */
  private renderContentItemImmediately(
    content_item: ModalContentItem,
    target_modal: HTMLElement | null,
  ): HTMLElement | null
  {
    this.mModalContentState.setCurrentItem(content_item);

    if (target_modal)
    {
      this.mMediaPlaybackController.stopVideosInElement(
        target_modal,
        this.mOptions.video.resetOnChange,
      );
    }

    const prepare_cloned_content = this.mOptions.video.autoplay
      ? (cloned_content: Node): void =>
      {
        this.mMediaPlaybackController.prepareVideoControlsInNode(cloned_content);
      }
      : null;
    const content_display: HTMLElement | null = this.mModalContentRenderer.render(
      content_item,
      target_modal,
      prepare_cloned_content,
    );

    this.updateContentNavButtonAvailability(target_modal);
    this.mModalContentIndicator.update(target_modal, content_item);
    this.mModalContentChooser.update(target_modal, content_item);

    if (content_display)
    {
      this.mMediaPlaybackController.playVideosInElement(
        content_display,
        this.mOptions.video.autoplay,
      );
    }

    this.mContentNavVisibilityController.startByRenderedContent(target_modal, content_display);

    return content_display;
  }

  /**
   * // Method: content switch fade を使うか判定する
   * // Note:
   * // - 初回 open 時は should_animate=false にして、open effect と content fade を重ねません。
   * // - reduced motion 時は待機せず即時差し替えにします。
   */
  private shouldUseContentSwitchEffect(
    content_display: HTMLElement | null,
    should_animate: boolean,
  ): content_display is HTMLElement
  {
    if (!should_animate || !content_display)
    {
      return false;
    }

    if (!this.shouldUseContentSwitchEffectByOptions())
    {
      return false;
    }

    return content_display.childNodes.length > 0;
  }

  /**
   * // Method: content switch effect を使う設定か判定する
   */
  private shouldUseContentSwitchEffectByOptions(): boolean
  {
    if (!this.mOptions.effect.content.enabled || this.mOptions.effect.content.name === 'none')
    {
      return false;
    }

    return this.canUseMotionEffect();
  }

  /**
   * // Method: content switch で実際に使う effect 名を解決する
   * // Note:
   * // - random は切替ごとに対象 effect から1つを選びます。
   * // - none は shouldUseContentSwitchEffectByOptions() で除外済みです。
   */
  private resolveContentSwitchEffectName(): ModalContentEffectName
  {
    const effect_name: ModalContentEffectName = this.mOptions.effect.content.name;

    if (effect_name !== 'random')
    {
      return effect_name;
    }

    const effect_names: ModalContentEffectName[] = ModalDisplay.RANDOM_CONTENT_EFFECT_NAMES;
    const random_index: number = Math.floor(Math.random() * effect_names.length);

    return effect_names[random_index] ?? 'fade';
  }

  /**
   * // Method: content switch effect 用の状態属性を付与する
   */
  private addContentSwitchEffectAttributes(
    effect_element: HTMLElement,
    effect_name: ModalContentEffectName,
  ): void
  {
    effect_element.setAttribute(ModalDisplay.ATTR_CONTENT_SWITCH_STATE, 'switching');

    if (effect_name === 'blur')
    {
      effect_element.setAttribute(ModalDisplay.ATTR_CONTENT_SWITCH_EFFECT, 'blur');
    }
  }

  /**
   * // Method: content switch effect 用の状態属性を解除する
   */
  private removeContentSwitchEffectAttributes(effect_element: HTMLElement | null): void
  {
    if (!effect_element)
    {
      return;
    }

    effect_element.removeAttribute(ModalDisplay.ATTR_CONTENT_SWITCH_STATE);
    effect_element.removeAttribute(ModalDisplay.ATTR_CONTENT_SWITCH_EFFECT);
  }

  /**
   * // Method: group 移動時に choose list も content と同じfade対象へ含める
   * // Note:
   * // - 同一group内の item 移動や choose 選択では、既存の current 表示更新を優先します。
   * // - group が変わる場合だけ list の中身が大きく差し替わるため、違和感を抑える対象にします。
   */
  private getContentSwitchChooseList(
    target_modal: HTMLElement | null,
    content_item: ModalContentItem,
    should_animate: boolean,
  ): HTMLElement | null
  {
    if (!should_animate || !this.shouldUseContentSwitchEffectByOptions())
    {
      return null;
    }

    const include_choose_list = this.mOptions.effect.content.includeChooseList;

    if (include_choose_list === 'never')
    {
      return null;
    }

    const current_item: ModalContentItem | null = this.mModalContentState.getCurrentItem();

    if (
      include_choose_list === 'auto' &&
      (!current_item || current_item.group_name === content_item.group_name)
    )
    {
      return null;
    }

    const choose_list: HTMLElement | null = this.mModalContentChooser.getChooseList(target_modal);

    if (!choose_list || choose_list.hidden || choose_list.childNodes.length === 0)
    {
      return null;
    }

    return choose_list;
  }

  /**
   * // Method: content の fade-out 完了後に差し替え処理を実行する
   */
  private waitForContentSwitchFadeOut(content_display: HTMLElement, callback: () => void): void
  {
    this.clearContentSwitchWait();

    const wait_time: number = this.getTransitionWaitTime(content_display);

    if (wait_time <= 0)
    {
      callback();
      return;
    }

    const finish = (): void =>
    {
      this.clearContentSwitchWait();
      callback();
    };

    const transition_end_listener = (event: TransitionEvent): void =>
    {
      if (event.target !== content_display)
      {
        return;
      }

      finish();
    };

    this.mContentSwitchDisplay = content_display;
    this.mContentSwitchTransitionEndListener = transition_end_listener;
    content_display.addEventListener('transitionend', transition_end_listener);

    this.mContentSwitchFallbackTimerId = window.setTimeout(
      finish,
      wait_time + ModalDisplay.TRANSITION_FALLBACK_DELAY,
    );
  }

  /**
   * // Method: clone 差し替え後の video 準備を待ってから content fade-in を開始する
   */
  private activateContentSwitchFadeInWhenReady(
    content_display: HTMLElement | null,
    content_switch_token: number,
  ): void
  {
    if (!content_display)
    {
      this.activateContentSwitchFadeIn(content_display);
      return;
    }

    this.mContentSwitchDisplay = content_display;

    this.mMediaPlaybackController.waitVisualMediaLayoutReadyInElement(content_display).then(() =>
    {
      if (!this.isCurrentContentSwitchToken(content_switch_token))
      {
        return;
      }

      if (this.mContentSwitchDisplay !== content_display)
      {
        return;
      }

      this.activateContentSwitchFadeIn(content_display);
    });
  }

  /**
   * // Method: clone 差し替え後の content fade-in を開始する
   */
  private activateContentSwitchFadeIn(content_display: HTMLElement | null): void
  {
    if (!content_display)
    {
      this.removeContentSwitchEffectAttributes(this.mContentSwitchChooseList);
      this.unlockContentSwitchDisplaySize();
      this.finishUiMotion();
      this.finishContentSwitch();
      this.mContentSwitchChooseList = null;
      return;
    }

    this.clearContentSwitchFrame();

    this.mContentSwitchDisplay = content_display;

    this.mContentSwitchFrameId = window.requestAnimationFrame(() =>
    {
      this.unlockContentSwitchDisplaySize();
      content_display.getBoundingClientRect();

      this.mContentSwitchFrameId = window.requestAnimationFrame(() =>
      {
        this.removeContentSwitchEffectAttributes(content_display);
        this.removeContentSwitchEffectAttributes(this.mContentSwitchChooseList);
        this.finishUiMotion();
        this.finishContentSwitch();
        this.mContentSwitchFrameId = null;
        this.mContentSwitchDisplay = null;
        this.mContentSwitchChooseList = null;
      });
    });
  }

  /**
   * // Method: content switch 中の表示領域サイズを一時的に保持する
   * // Note:
   * // - fade-out 中だけ差し替え前の高さを px で固定します。
   * // - clone 差し替え後は opacity: 0 のまま固定を解除し、自然サイズへ戻してから fade-in します。
   * // - inline style は fade-in 前、または cancel 時に解除します。
   */
  private lockContentSwitchDisplaySize(content_display: HTMLElement): void
  {
    this.unlockContentSwitchDisplaySize();

    const display_rect: DOMRect = content_display.getBoundingClientRect();

    if (display_rect.height <= 0)
    {
      return;
    }

    this.mContentSwitchLockedDisplay = content_display;
    content_display.setAttribute(ModalDisplay.ATTR_CONTENT_SIZE_STATE, 'animating');
    content_display.style.setProperty(
      ModalDisplay.CSS_MODAL_CONTENT_SWITCH_BLOCK_SIZE,
      `${display_rect.height}px`,
    );
  }

  /**
   * // Method: content switch 用の表示領域サイズ保持を解除する
   */
  private unlockContentSwitchDisplaySize(): void
  {
    this.mContentSwitchLockedDisplay?.removeAttribute(ModalDisplay.ATTR_CONTENT_SIZE_STATE);
    this.mContentSwitchLockedDisplay?.style.removeProperty(
      ModalDisplay.CSS_MODAL_CONTENT_SWITCH_BLOCK_SIZE,
    );
    this.mContentSwitchLockedDisplay = null;
  }

  /**
   * // Method: content switch 処理の識別番号を更新する
   */
  private createContentSwitchToken(): number
  {
    this.mContentSwitchToken += 1;

    return this.mContentSwitchToken;
  }

  /**
   * // Method: 現在の content switch 処理か判定する
   */
  private isCurrentContentSwitchToken(content_switch_token: number): boolean
  {
    return this.mContentSwitchToken === content_switch_token;
  }

  /**
   * // Method: content 差し替え中の UI motion を開始する
   * // Note:
   * // - 画像サイズ差で control / indicator の位置が急に変わる違和感を、fadeまたはfade+moveで軽減します。
   */
  private startUiMotion(target_modal: HTMLElement | null): void
  {
    if (!this.shouldUseUiMotion(target_modal))
    {
      return;
    }

    this.mUiMotionModal = target_modal;
    target_modal.setAttribute(ModalDisplay.ATTR_UI_MOTION_STATE, 'switching');
  }

  /**
   * // Method: content 差し替え後の UI motion を終了する
   */
  private finishUiMotion(): void
  {
    this.mUiMotionModal?.removeAttribute(ModalDisplay.ATTR_UI_MOTION_STATE);
    this.mUiMotionModal = null;
  }

  /**
   * // Method: content switch 中の操作ロックを解除する
   * // Note:
   * // - UI motion を無効化してボタンを常時表示する場合でも、切替完了前の連打はここで吸収します。
   */
  private finishContentSwitch(): void
  {
    const content_switch_modal: HTMLElement | null = this.mContentSwitchModal;

    this.mIsContentSwitching = false;
    this.mContentSwitchModal = null;

    this.updateContentNavButtonAvailability(content_switch_modal);
  }

  /**
   * // Method: UI motion を実行してよい状態か判定する
   */
  private shouldUseUiMotion(target_modal: HTMLElement | null): target_modal is HTMLElement
  {
    if (!target_modal)
    {
      return false;
    }

    if (!this.mOptions.uiMotion.enabled)
    {
      return false;
    }

    if (!this.canUseMotionEffect())
    {
      return false;
    }

    return true;
  }

  /**
   * // Method: ナビゲーション種別から移動先のコンテンツを取得する
   * // Note:
   * // - next-item / prev-item / next-group / prev-group の実処理は ModalContentNavigator へ委譲します。
   */
  private getContentItemByNavigationType(
    navigation_type: ModalContentNavigationType,
  ): ModalContentItem | null
  {
    return this.mContentNavigationItemGetters[navigation_type]();
  }

  /**
   * // Method: 現在表示中の content 状態に応じてナビゲーションボタンの表示状態を更新する
   * // Note:
   * // - ここでの表示状態は、移動可能かどうかに応じた hidden / disabled の更新です。
   * // - options で無効化された item / group ボタンも hidden / disabled にします。
   * // - loop が有効な移動種別では、先頭 / 末尾でも折り返し先があれば有効状態を維持します。
   * // - 遅延表示や video 終了後の再表示は ModalContentNavVisibilityController が担当します。
   */
  private updateContentNavButtonAvailability(target_modal: HTMLElement | null): void
  {
    const nav_buttons: HTMLElement[] =
      this.mModalControl.getContentNavButtonsByModal(target_modal);

    nav_buttons.forEach((nav_button: HTMLElement) =>
    {
      const navigation_action: ModalContentNavigationAction | null =
        this.mModalControl.getContentNavigationActionByElement(nav_button);
      const navigation_type: ModalContentNavigationType | null =
        this.resolveContentNavigationTypeByAction(navigation_action);

      if (!navigation_type)
      {
        this.disableContentNavButton(nav_button);
        return;
      }

      if (!this.isContentNavButtonNavigationTypeEnabled(navigation_type))
      {
        this.disableContentNavButton(nav_button);
        return;
      }

      const content_item: ModalContentItem | null =
        this.getContentItemByNavigationType(navigation_type);

      const is_available: boolean = content_item !== null;

      if (!is_available)
      {
        this.disableContentNavButton(nav_button);
        return;
      }

      this.enableContentNavButton(nav_button);
    });
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンを有効状態にする
   */
  private enableContentNavButton(nav_button: HTMLElement): void
  {
    nav_button.hidden = false;
    nav_button.toggleAttribute('aria-hidden', false);

    if (nav_button instanceof HTMLButtonElement)
    {
      nav_button.disabled = false;
    }
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンを無効状態にする
   */
  private disableContentNavButton(nav_button: HTMLElement): void
  {
    nav_button.hidden = true;
    nav_button.toggleAttribute('aria-hidden', true);

    if (nav_button instanceof HTMLButtonElement)
    {
      nav_button.disabled = true;
    }
  }

  /**
   * // Method: モーダルをすべて閉じ、汎用コンテンツの状態も初期化する
   * // Note:
   * // - 閉じる時点でタイマー・ボタン表示・動画再生・現在 item 状態をまとめてリセットします。
   */
  private closeAll(): void
  {
    if (this.mIsClosing)
    {
      return;
    }

    if (!this.hasActiveModal())
    {
      return;
    }

    this.clearEffectReadyFrame();
    this.cancelContentSwitchEffect();
    this.prepareCloseAll();

    const active_modal: HTMLElement | null = this.getActiveModal();

    if (!this.shouldWaitForCloseEffect(active_modal))
    {
      this.finishCloseAll();
      return;
    }

    this.startCloseEffect(active_modal);
  }

  /**
   * // Method: close 開始時に即時停止・非表示に寄せる処理をまとめる
   */
  private prepareCloseAll(): void
  {
    this.mContentNavVisibilityController.clearTimer();

    this.mModalControl.getAllModals().forEach((modal: HTMLElement) =>
    {
      this.mContentNavVisibilityController.hide(modal);
      this.mMediaPlaybackController.stopVideosInElement(
        modal,
        this.mOptions.video.resetOnChange,
      );
    });
  }

  /**
   * // Method: close effect の transition 待機を開始する
   */
  private startCloseEffect(active_modal: HTMLElement): void
  {
    this.clearCloseEffectWait();

    this.mIsClosing = true;
    this.mClosingModal = active_modal;
    active_modal.setAttribute(ModalDisplay.ATTR_EFFECT_STATE, 'closing');

    const wait_time: number = this.getTransitionWaitTime(active_modal);

    if (wait_time <= 0)
    {
      this.finishCloseAll();
      return;
    }

    const finish = (): void =>
    {
      this.finishCloseAll();
    };

    const transition_end_listener = (event: TransitionEvent): void =>
    {
      if (event.target !== active_modal)
      {
        return;
      }

      finish();
    };

    this.mCloseTransitionEndListener = transition_end_listener;
    active_modal.addEventListener('transitionend', transition_end_listener);

    this.mCloseFallbackTimerId = window.setTimeout(
      finish,
      wait_time + ModalDisplay.TRANSITION_FALLBACK_DELAY,
    );
  }

  /**
   * // Method: close effect 後の実際の非表示・復帰処理を行う
   */
  private finishCloseAll(): void
  {
    this.clearCloseEffectWait();

    this.mModalContentChooser.clear(this.mModalControl.getAllModals());

    this.mModalState.closeAll();
    this.mModalContentState.reset();

    this.resetAllModalEffectStates();
    this.mIsClosing = false;
  }

  /**
   * // Method: 表示中のモーダルを取得する
   */
  private getActiveModal(): HTMLElement | null
  {
    return this.mModalControl.getAllModals().find((modal: HTMLElement) =>
    {
      return this.mModalControl.isActiveModal(modal);
    }) ?? null;
  }

  /**
   * // Method: close 完了を transition 待ちにするか判定する
   */
  private shouldWaitForCloseEffect(active_modal: HTMLElement | null): active_modal is HTMLElement
  {
    if (!active_modal)
    {
      return false;
    }

    if (!this.shouldUseModalEffect() || !this.canUseMotionEffect())
    {
      return false;
    }

    return this.getTransitionWaitTime(active_modal) > 0;
  }

  /**
   * // Method: close 待機中の transitionend / timer を解除する
   */
  private clearCloseEffectWait(): void
  {
    if (this.mClosingModal && this.mCloseTransitionEndListener)
    {
      this.mClosingModal.removeEventListener('transitionend', this.mCloseTransitionEndListener);
    }

    if (this.mCloseFallbackTimerId !== null)
    {
      window.clearTimeout(this.mCloseFallbackTimerId);
    }

    this.mCloseTransitionEndListener = null;
    this.mCloseFallbackTimerId = null;
    this.mClosingModal = null;
  }

  /**
   * // Method: close 待機を取り消す
   */
  private cancelCloseEffect(): void
  {
    const closing_modal: HTMLElement | null = this.mClosingModal;

    this.clearCloseEffectWait();

    if (closing_modal)
    {
      closing_modal.removeAttribute(ModalDisplay.ATTR_EFFECT_STATE);
    }

    this.mIsClosing = false;
  }

  /**
   * // Method: content switch 待機中の transitionend / timer を解除する
   */
  private clearContentSwitchWait(): void
  {
    if (this.mContentSwitchDisplay && this.mContentSwitchTransitionEndListener)
    {
      this.mContentSwitchDisplay.removeEventListener(
        'transitionend',
        this.mContentSwitchTransitionEndListener,
      );
    }

    if (this.mContentSwitchFallbackTimerId !== null)
    {
      window.clearTimeout(this.mContentSwitchFallbackTimerId);
    }

    this.mContentSwitchTransitionEndListener = null;
    this.mContentSwitchFallbackTimerId = null;
    this.mContentSwitchDisplay = null;
  }

  /**
   * // Method: content switch fade-in 待機 frame を解除する
   */
  private clearContentSwitchFrame(): void
  {
    if (this.mContentSwitchFrameId === null)
    {
      return;
    }

    window.cancelAnimationFrame(this.mContentSwitchFrameId);
    this.mContentSwitchFrameId = null;
  }

  /**
   * // Method: content switch 待機を取り消す
   */
  private cancelContentSwitchEffect(): void
  {
    const content_display: HTMLElement | null = this.mContentSwitchDisplay;
    const choose_list: HTMLElement | null = this.mContentSwitchChooseList;

    this.clearContentSwitchWait();
    this.clearContentSwitchFrame();
    this.createContentSwitchToken();

    this.removeContentSwitchEffectAttributes(content_display);
    this.removeContentSwitchEffectAttributes(choose_list);
    this.finishUiMotion();
    this.unlockContentSwitchDisplaySize();

    this.mContentSwitchDisplay = null;
    this.mContentSwitchChooseList = null;
    this.mContentSwitchModal = null;
    this.mIsContentSwitching = false;
  }

  /**
   * // Method: motion effect を実行してよい状態か判定する
   */
  private canUseMotionEffect(): boolean
  {
    return canUseMotion(this.mOptions.respectUserMotionPreference);
  }

  /**
   * // Method: transition-duration / delay から待機時間を取得する
   */
  private getTransitionWaitTime(element: HTMLElement): number
  {
    const computed_style: CSSStyleDeclaration = window.getComputedStyle(element);
    const transition_durations: number[] = this.parseTransitionTimeList(computed_style.transitionDuration);
    const transition_delays: number[] = this.parseTransitionTimeList(computed_style.transitionDelay);
    const time_count: number = Math.max(transition_durations.length, transition_delays.length);

    let max_time = 0;

    for (let time_index = 0; time_index < time_count; time_index += 1)
    {
      const duration: number = transition_durations[time_index] ?? transition_durations[0] ?? 0;
      const delay: number = transition_delays[time_index] ?? transition_delays[0] ?? 0;
      const total_time: number = duration + delay;

      if (total_time > max_time)
      {
        max_time = total_time;
      }
    }

    return max_time;
  }

  /**
   * // Method: CSS time list を millisecond 配列へ変換する
   */
  private parseTransitionTimeList(value: string): number[]
  {
    return value.split(',').map((time_value: string): number =>
    {
      const trimmed_value: string = time_value.trim();

      if (trimmed_value.endsWith('ms'))
      {
        return Number.parseFloat(trimmed_value);
      }

      if (trimmed_value.endsWith('s'))
      {
        return Number.parseFloat(trimmed_value) * 1000;
      }

      return 0;
    });
  }
}
